import React, { useState, useEffect } from 'react';
import { 
  Users, UserCheck, CreditCard, Activity, TrendingUp, MessageSquare, Heart,
  AlertTriangle, Shield, CheckCircle, XCircle, Download, Filter, Calendar,
  UserPlus, Crown, Flag, FileText, DollarSign, BarChart3, TrendingDown
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const COLORS = {
  primary: '#ef4444',
  secondary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#dc2626',
  purple: '#8b5cf6',
  pink: '#ec4899'
};

const AdminDashboardNew = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/stats?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load dashboard statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    if (!stats) return;
    
    const csvData = `
Dashboard Statistics Export
Generated: ${format(new Date(), 'PPpp')}

Total Users,${stats.totalUsers}
Premium Users,${stats.premiumUsers}
Free Users,${stats.freeUsers}
Verified Users,${stats.verifiedUsers}
Active Users (7 days),${stats.activeUsers}
Total Messages,${stats.totalMessages}
Total Interests,${stats.totalInterests}
Pending Reports,${stats.pendingReports}
Pending Verifications,${stats.pendingVerifications}
Total Revenue,$${stats.totalRevenue?.toFixed(2)}
    `.trim();

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qbool-dashboard-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Dashboard data has been exported to CSV",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-mint-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Calculate insights
  const userGrowth = stats?.monthlyGrowth ? 
    ((stats.monthlyGrowth[stats.monthlyGrowth.length - 1]?.users || 0) - 
     (stats.monthlyGrowth[stats.monthlyGrowth.length - 2]?.users || 0)) : 0;
  
  const premiumConversionRate = stats?.totalUsers > 0 ? 
    ((stats.premiumUsers / stats.totalUsers) * 100).toFixed(1) : 0;

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      trend: userGrowth > 0 ? 'up' : userGrowth < 0 ? 'down' : 'neutral',
      trendValue: `${Math.abs(userGrowth)} this month`
    },
    {
      title: 'Premium Members',
      value: stats?.premiumUsers || 0,
      icon: Crown,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      subtitle: `${premiumConversionRate}% conversion rate`
    },
    {
      title: 'Verified Users',
      value: stats?.verifiedUsers || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      subtitle: `${stats?.unverifiedUsers || 0} pending`
    },
    {
      title: 'Active Users (7d)',
      value: stats?.activeUsers || 0,
      icon: Activity,
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      subtitle: `${((stats?.activeUsers / stats?.totalUsers) * 100).toFixed(0)}% of total`
    },
    {
      title: 'Total Revenue',
      value: `$${stats?.totalRevenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'bg-emerald-500',
      textColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      subtitle: `$${stats?.monthlyRevenue?.toFixed(2)} this month`
    },
    {
      title: 'Messages Sent',
      value: stats?.totalMessages || 0,
      icon: MessageSquare,
      color: 'bg-pink-500',
      textColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      subtitle: `${stats?.messagesInPeriod || 0} in period`
    },
    {
      title: 'Interests Shared',
      value: stats?.totalInterests || 0,
      icon: Heart,
      color: 'bg-cream-1000',
      textColor: 'text-navy-500',
      bgColor: 'bg-cream-100',
      subtitle: `${stats?.interestsInPeriod || 0} in period`
    },
    {
      title: 'Pending Reports',
      value: stats?.pendingReports || 0,
      icon: Flag,
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      subtitle: `${stats?.totalReports || 0} total reports`
    }
  ];

  const quickActions = [
    { 
      label: 'View Pending Profiles', 
      icon: UserCheck, 
      color: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => navigate('/admin/profiles')
    },
    { 
      label: 'View Reports', 
      icon: AlertTriangle, 
      color: 'bg-orange-600 hover:bg-orange-700',
      onClick: () => navigate('/admin/reports')
    },
    { 
      label: 'Manage Users', 
      icon: Users, 
      color: 'bg-purple-600 hover:bg-purple-700',
      onClick: () => navigate('/admin/users')
    },
    { 
      label: 'Verifications', 
      icon: Shield, 
      color: 'bg-green-600 hover:bg-green-700',
      onClick: () => navigate('/admin/verifications')
    }
  ];

  const genderData = [
    { name: 'Male', value: stats?.maleUsers || 0, color: COLORS.secondary },
    { name: 'Female', value: stats?.femaleUsers || 0, color: COLORS.pink }
  ];

  const subscriptionData = [
    { name: 'Premium', value: stats?.premiumUsers || 0, color: COLORS.success },
    { name: 'Free', value: stats?.freeUsers || 0, color: COLORS.warning }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Complete platform analytics and management</p>
            </div>
            
            <div className="flex gap-3 mt-4 md:mt-0">
              {/* Date Range Filter */}
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              >
                <option value={7}>Last 7 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={90}>Last 90 Days</option>
              </select>

              {/* Export Button */}
              <Button
                onClick={exportData}
                className="bg-gray-800 hover:bg-gray-900 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mt-6">
        {/* Smart Insights */}
        <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-lg p-6 text-white mb-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2">💡 Smart Insights</h2>
              <div className="space-y-2">
                {userGrowth > 0 && (
                  <p className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    User registrations increased by {userGrowth} this month
                  </p>
                )}
                {stats?.premiumUsers > 0 && (
                  <p className="flex items-center gap-2">
                    <Crown className="w-5 h-5" />
                    {premiumConversionRate}% of users have premium memberships
                  </p>
                )}
                {stats?.pendingReports > 0 && (
                  <p className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {stats.pendingReports} reports require your attention
                  </p>
                )}
              </div>
            </div>
            <BarChart3 className="w-16 h-16 opacity-50" />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`${action.color} text-white p-4 rounded-lg flex flex-col items-center justify-center gap-2 transition-all transform hover:scale-105 shadow-md`}
            >
              <action.icon className="w-6 h-6" />
              <span className="text-sm font-medium text-center">{action.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {statCards.map((card, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mb-1">{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</p>
                    {card.subtitle && (
                      <p className="text-xs text-gray-500">{card.subtitle}</p>
                    )}
                    {card.trend && (
                      <p className={`text-xs flex items-center gap-1 mt-1 ${
                        card.trend === 'up' ? 'text-green-600' : card.trend === 'down' ? 'text-navy-500' : 'text-gray-500'
                      }`}>
                        {card.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : card.trend === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
                        {card.trendValue}
                      </p>
                    )}
                  </div>
                  <div className={`${card.bgColor} p-3 rounded-lg`}>
                    <card.icon className={`w-6 h-6 ${card.textColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* User Growth Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly User Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats?.monthlyGrowth || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="users" stroke={COLORS.primary} strokeWidth={2} name="Total Users" />
                  <Line type="monotone" dataKey="premium" stroke={COLORS.success} strokeWidth={2} name="Premium Users" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Weekly Activity Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.weeklyActivity || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="messages" fill={COLORS.pink} name="Messages" />
                  <Bar dataKey="interests" fill={COLORS.primary} name="Interests" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Pie Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Gender Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Gender Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                  <span className="text-sm">Male: {stats?.maleUsers || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-pink-500"></div>
                  <span className="text-sm">Female: {stats?.femaleUsers || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={subscriptionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {subscriptionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500"></div>
                  <span className="text-sm">Premium: {stats?.premiumUsers || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                  <span className="text-sm">Free: {stats?.freeUsers || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Registrations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Recent Registrations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recentRegistrations && stats.recentRegistrations.length > 0 ? (
                  stats.recentRegistrations.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {user.date ? format(new Date(user.date), 'MMM dd, HH:mm') : 'N/A'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent registrations</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Premium Subscriptions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                Recent Premium Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recentSubscriptions && stats.recentSubscriptions.length > 0 ? (
                  stats.recentSubscriptions.map((user, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {user.date ? format(new Date(user.date), 'MMM dd, HH:mm') : 'N/A'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent subscriptions</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardNew;
